import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  ChartBarIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import apiClient from '../services/api';
import DashboardLayout from '../components/DashboardLayout';

const PatientEvolution = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState(null);
  const [evolutionData, setEvolutionData] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState(4000); // Frequência de 4kHz (mais importante para PAIR)

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Buscar dados do paciente
      const patientResponse = await apiClient.get(`/patients/${id}`);
      setPatient(patientResponse.data);

      // Buscar dados de evolução
      const evolutionResponse = await apiClient.get(`/patients/${id}/evolution`);
      setEvolutionData(evolutionResponse.data);

    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados de evolução');
    } finally {
      setLoading(false);
    }
  };

  // Formatar data para exibição
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Preparar dados para o gráfico de evolução por frequência
  const prepareChartData = (frequency) => {
    if (!evolutionData || !evolutionData.exams) return [];

    // Para cada exame, buscar o limiar na frequência selecionada
    // Nota: Como não temos os dados completos de cada exame no resultado,
    // vamos usar os PTAs por enquanto. Na versão completa, você deve retornar
    // os dados de Air/Bone para cada exame no backend.
    
    return evolutionData.exams.map((exam, index) => ({
      date: formatDate(exam.date),
      examNumber: index + 1,
      ptaOD: Math.round(exam.ptaRightEar),
      ptaOE: Math.round(exam.ptaLeftEar)
    }));
  };

  // Classificar grau de perda auditiva baseado no PTA
  const classifyHearingLoss = (pta) => {
    if (pta <= 25) return { label: 'Normal', color: 'text-green-600', bg: 'bg-green-100' };
    if (pta <= 40) return { label: 'Leve', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (pta <= 55) return { label: 'Moderado', color: 'text-orange-600', bg: 'bg-orange-100' };
    if (pta <= 70) return { label: 'Severo', color: 'text-red-600', bg: 'bg-red-100' };
    return { label: 'Profundo', color: 'text-purple-600', bg: 'bg-purple-100' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando dados de evolução...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
            <button
              onClick={() => navigate('/patients')}
              className="mt-4 text-red-700 underline hover:text-red-800"
            >
              Voltar para lista de pacientes
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!evolutionData?.hasMultipleExams) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate('/patients')}
              className="mr-4 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              Evolução Audiométrica - {patient?.name}
            </h1>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                  Dados Insuficientes para Análise de Evolução
                </h3>
                <p className="text-yellow-700">{evolutionData?.message}</p>
                <p className="text-yellow-700 mt-2">
                  Total de exames encontrados: <strong>{evolutionData?.totalExams || 0}</strong>
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <Link
                to={`/report-wizard`}
                className="btn-primary"
              >
                Criar Novo Exame
              </Link>
              <button
                onClick={() => navigate('/patients')}
                className="btn-secondary"
              >
                Voltar para Pacientes
              </button>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const chartData = prepareChartData(selectedFrequency);
  const firstExam = evolutionData.exams[0];
  const lastExam = evolutionData.exams[evolutionData.exams.length - 1];
  
  const firstClassificationOD = classifyHearingLoss(firstExam.ptaRightEar);
  const firstClassificationOE = classifyHearingLoss(firstExam.ptaLeftEar);
  const lastClassificationOD = classifyHearingLoss(lastExam.ptaRightEar);
  const lastClassificationOE = classifyHearingLoss(lastExam.ptaLeftEar);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/patients')}
              className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">
                Análise de Evolução Audiométrica
              </h1>
              <div className="flex items-center text-gray-600">
                <UserIcon className="h-5 w-5 mr-2" />
                <span className="text-lg">{patient?.name}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alertas Portaria 19 */}
        {evolutionData.hasWorseningPortaria19 && (
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg mb-6 shadow-lg">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600 mr-3 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-bold text-red-800 mb-3">
                  ⚠️ AGRAVAMENTO DETECTADO (Portaria 19/98)
                </h3>
                <ul className="space-y-2">
                  {evolutionData.alerts.map((alert, index) => (
                    <li key={index} className="text-red-700 font-medium">
                      {alert}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-red-600 font-semibold">
                  📋 Recomenda-se avaliação clínica detalhada e medidas de controle conforme NR-7 e Portaria 19.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <CalendarIcon className="h-8 w-8 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Total de Exames</p>
            <p className="text-3xl font-bold text-blue-900">{evolutionData.totalExams}</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <CalendarIcon className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Primeiro Exame</p>
            <p className="text-lg font-bold text-green-900">
              {formatDate(evolutionData.firstExamDate)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <CalendarIcon className="h-8 w-8 text-purple-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Último Exame</p>
            <p className="text-lg font-bold text-purple-900">
              {formatDate(evolutionData.lastExamDate)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-md">
            <div className="flex items-center justify-between mb-2">
              <ArrowTrendingUpIcon className="h-8 w-8 text-orange-600" />
            </div>
            <p className="text-sm text-gray-600 mb-1">Período de Monitoramento</p>
            <p className="text-3xl font-bold text-orange-900">
              {evolutionData.monitoringPeriodDays} dias
            </p>
          </div>
        </div>

        {/* Gráfico de Evolução de PTAs */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Evolução dos Limiares Auditivos (PTA)
              </h2>
              <p className="text-gray-600 text-sm">
                Média Tritonal (500Hz, 1000Hz, 2000Hz) - Orelha Direita e Esquerda
              </p>
            </div>
            <ChartBarIcon className="h-10 w-10 text-medical-600" />
          </div>

          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                label={{ value: 'Data do Exame', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                label={{ value: 'Limiar (dB NA)', angle: -90, position: 'insideLeft' }}
                domain={[0, 120]}
                reversed
              />
              <Tooltip />
              <Legend />
              <ReferenceLine y={25} stroke="#10b981" strokeDasharray="3 3" label="Normal (≤25dB)" />
              <ReferenceLine y={40} stroke="#f59e0b" strokeDasharray="3 3" label="Leve (≤40dB)" />
              <ReferenceLine y={55} stroke="#ef4444" strokeDasharray="3 3" label="Moderado (≤55dB)" />
              
              <Line 
                type="monotone" 
                dataKey="ptaOD" 
                stroke="#2563eb" 
                strokeWidth={3}
                name="PTA Orelha Direita"
                dot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="ptaOE" 
                stroke="#dc2626" 
                strokeWidth={3}
                name="PTA Orelha Esquerda"
                dot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Comparação Primeiro vs Último Exame */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Primeiro Exame */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              📅 Primeiro Exame - {formatDate(firstExam.date)}
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Orelha Direita (OD)</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900">
                    {Math.round(firstExam.ptaRightEar)} dB
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${firstClassificationOD.bg} ${firstClassificationOD.color}`}>
                    {firstClassificationOD.label}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Orelha Esquerda (OE)</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900">
                    {Math.round(firstExam.ptaLeftEar)} dB
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${firstClassificationOE.bg} ${firstClassificationOE.color}`}>
                    {firstClassificationOE.label}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Último Exame */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              📅 Último Exame - {formatDate(lastExam.date)}
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">Orelha Direita (OD)</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900">
                    {Math.round(lastExam.ptaRightEar)} dB
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${lastClassificationOD.bg} ${lastClassificationOD.color}`}>
                    {lastClassificationOD.label}
                  </span>
                </div>
                {lastExam.ptaRightEar !== firstExam.ptaRightEar && (
                  <p className={`text-sm mt-2 font-medium ${
                    lastExam.ptaRightEar > firstExam.ptaRightEar ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {lastExam.ptaRightEar > firstExam.ptaRightEar ? '▲' : '▼'} 
                    {Math.abs(Math.round(lastExam.ptaRightEar - firstExam.ptaRightEar))} dB
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">Orelha Esquerda (OE)</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-gray-900">
                    {Math.round(lastExam.ptaLeftEar)} dB
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${lastClassificationOE.bg} ${lastClassificationOE.color}`}>
                    {lastClassificationOE.label}
                  </span>
                </div>
                {lastExam.ptaLeftEar !== firstExam.ptaLeftEar && (
                  <p className={`text-sm mt-2 font-medium ${
                    lastExam.ptaLeftEar > firstExam.ptaLeftEar ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {lastExam.ptaLeftEar > firstExam.ptaLeftEar ? '▲' : '▼'} 
                    {Math.abs(Math.round(lastExam.ptaLeftEar - firstExam.ptaLeftEar))} dB
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/patients')}
            className="btn-secondary"
          >
            Voltar para Pacientes
          </button>
          <Link
            to={`/report-wizard`}
            className="btn-primary"
          >
            Criar Novo Exame
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PatientEvolution;
